import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import BaseForm from 'components/atoms/base-form';
import BaseFormGroup from 'components/atoms/base-form/group';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseGrid from 'components/atoms/base-grid';
import BaseInput from 'components/atoms/base-input';
import ContactMethodSelectBox from 'components/molecules/contact-methods-select-box';
import BaseDatepicker from 'components/atoms/base-datepicker';
import BaseExpandAnimation from 'components/atoms/base-expand-animation';
import classNames from 'classnames';
import { mergeFieldsValue } from 'infrastructure/functions';
import BaseButton from 'components/atoms/baseButton';
import PlusIcon from 'components/atoms/icons/plus';
import MinusIcon from 'components/atoms/icons/minus';
import BaseRichTextEditor from 'components/atoms/base-rich-text-editor';
import DoNotCallRestrictionTypeSelectBox from 'components/molecules/do-not-call-restriction-type-select-box';
import { ClinicDoNotCallRestrictionType } from 'infrastructure/enums';
import TimeSelectBox from 'components/molecules/time-select-box';
import useUpdateEffect from 'infrastructure/hooks/utils/use-update-effect';

import s from './styles.module.scss';
import { clinicInstructionsValidationSchema } from './validation-schema';

import type { Clinic } from 'infrastructure/classes/clinic';
import type {
  IClinicInstructions,
  IClinicMappings,
} from 'infrastructure/interfaces';
import type {
  IIndependentFormProps,
  IIndependentFormRef,
} from 'infrastructure/interfaces/common/i-independent-form';

const prepareValue = (data?: Clinic): IClinicInstructions => {
  let defaultValue: IClinicInstructions = {
    doNotCallRestrictionType: data?.isCcmDoNotCallParentInherit
      ? ClinicDoNotCallRestrictionType.InheritParentClinic
      : null,
    ccmDoNotCallRestrictionType: data?.isDoNotCallParentInherit
      ? ClinicDoNotCallRestrictionType.InheritParentClinic
      : null,
    doNotCallStartTime: null,
    doNotCallEndTime: null,
    ccmDoNotCallStartTime: null,
    ccmDoNotCallEndTime: null,
    criticalBusinessHours: null,
    criticalAfterHours: null,
    afterHours: null,
    businessHours: null,
    contactMethod: null,
    contactMethodValue: null,
    notes: '',
    dateNoteLogged: null,
  };
  if (data) {
    defaultValue = mergeFieldsValue(defaultValue, data);
  }

  return defaultValue;
};

const FormDisplayName = 'ClinicInstructionsForm';

interface IProps extends IIndependentFormProps {
  data?: Clinic;
  clinicMappings?: Partial<IClinicMappings>;
}

const ClinicInstructionsForm = forwardRef<
  IIndependentFormRef<IClinicInstructions>,
  IProps
>((props, ref) => {
  const { data, clinicMappings } = props;

  const { t } = useTranslation();

  const [opened, setOpened] = useState(false);

  const formik = useFormik<IClinicInstructions>({
    initialValues: prepareValue(data),
    onSubmit: () => {},
    validateOnChange: false,
    validationSchema: opened ? clinicInstructionsValidationSchema() : undefined,
  });

  const { values } = formik;

  const isParentClinic = !clinicMappings?.parentClinicGuid;
  const dncRestrictionType = isParentClinic ? 'patient' : 'clinic';

  const isRpmLimitedHours =
    values.doNotCallRestrictionType ===
    ClinicDoNotCallRestrictionType.LimitedHours;
  const isCcmLimitedHours =
    values.ccmDoNotCallRestrictionType ===
    ClinicDoNotCallRestrictionType.LimitedHours;

  const groupClassNames = classNames(s.group, {
    [s.opened]: !opened,
  });

  const controlBtn = useMemo(() => {
    return (
      <BaseButton
        dataCy="clinicInstructions-expand-button"
        rounded
        icon={opened ? <MinusIcon /> : <PlusIcon />}
        size="large"
      />
    );
  }, [opened]);

  const onToggle = () => {
    setOpened((prev) => !prev);
  };

  const submitForm = async () => {
    await formik.submitForm();
    const isValid = await formik
      .validateForm()
      .then((res) => !Object.keys(res).length);

    return {
      values: opened ? formik.values : prepareValue(),
      formName: FormDisplayName,
      isValid: opened ? isValid : true,
    };
  };

  useImperativeHandle(ref, () => ({
    submit: submitForm,
    reset: formik.resetForm,
  }));

  useEffect(() => {
    const val = prepareValue(data);
    const objCopy = {
      ...val,
      notes: val.notes === '' ? undefined : val.notes,
      dateNoteLogged: undefined,
    };
    const hasValue = Object.values(objCopy).some((el) => Boolean(el));
    setOpened(hasValue);
    if (data) formik.resetForm({ values: val });
  }, [data]);

  useUpdateEffect(() => {
    formik.setFieldValue(
      'doNotCallRestrictionType',
      data?.isDoNotCallParentInherit
        ? ClinicDoNotCallRestrictionType.InheritParentClinic
        : data?.doNotCallRestrictionType ?? null,
    );
    formik.setFieldValue(
      'ccmDoNotCallRestrictionType',
      data?.isCcmDoNotCallParentInherit
        ? ClinicDoNotCallRestrictionType.InheritParentClinic
        : data?.ccmDoNotCallRestrictionType ?? null,
    );
  }, [clinicMappings?.parentClinicGuid, data]);

  return (
    <BaseForm formik={formik}>
      <BaseFormGroup
        label={t('labels.clinicInstructions')}
        className={groupClassNames}
        contentClassName={s.content}
        onHeaderClick={onToggle}
        controls={controlBtn}
      >
        <BaseExpandAnimation opened={opened}>
          <BaseGrid columns={2} columnGap={0}>
            <div className={s.col}>
              <BaseGrid rowGap={12}>
                <BaseFormItem
                  name="criticalBusinessHours"
                  label={t('labels.criticalBusinessHours')}
                  isBlackLabel
                >
                  <BaseRichTextEditor
                    value={values.criticalBusinessHours ?? ''}
                    setValue={(value) => {
                      formik.setFieldValue('criticalBusinessHours', value);
                    }}
                    disabled={false}
                  />
                </BaseFormItem>
                <BaseFormItem
                  name="criticalAfterHours"
                  label={t('labels.criticalAfterHours')}
                  isBlackLabel
                >
                  <BaseRichTextEditor
                    value={values.criticalAfterHours ?? ''}
                    setValue={(value) => {
                      formik.setFieldValue('criticalAfterHours', value);
                    }}
                    disabled={false}
                  />
                </BaseFormItem>
              </BaseGrid>
            </div>
            <div className={s.col}>
              <BaseGrid columns={2} columnGap={32}>
                <BaseFormItem
                  name="businessHours"
                  label={t('labels.businessHours')}
                  isBlackLabel
                >
                  <BaseInput />
                </BaseFormItem>
                <BaseFormItem
                  name="afterHours"
                  label={t('labels.afterHours')}
                  isBlackLabel
                >
                  <BaseInput />
                </BaseFormItem>
                <BaseFormItem
                  name="contactMethod"
                  label={t('labels.contactMethod')}
                  isBlackLabel
                >
                  <ContactMethodSelectBox />
                </BaseFormItem>
                <BaseFormItem
                  name="contactMethodValue"
                  label={t('labels.contactMethodValue')}
                  isBlackLabel
                >
                  <BaseInput />
                </BaseFormItem>
                <BaseFormItem
                  name="dateNoteLogged"
                  label={t('labels.dateNote')}
                  isBlackLabel
                  required
                >
                  <BaseDatepicker
                    onChange={(_, date) => {
                      formik.setFieldValue('dateNoteLogged', date || undefined);
                    }}
                  />
                </BaseFormItem>
              </BaseGrid>

              <BaseGrid columns={isRpmLimitedHours ? 2 : 1}>
                <BaseFormItem
                  name="doNotCallRestrictionType"
                  label={`RPM ${t('labels.doNotCallRestrictionType')}`}
                  isBlackLabel
                >
                  <DoNotCallRestrictionTypeSelectBox
                    type={dncRestrictionType}
                    withNull
                  />
                </BaseFormItem>
                {isRpmLimitedHours && (
                  <BaseGrid columns={2}>
                    <BaseFormItem
                      name="doNotCallStartTime"
                      label={t('labels.startTime')}
                      required
                      isBlackLabel
                    >
                      <TimeSelectBox />
                    </BaseFormItem>
                    <BaseFormItem
                      name="doNotCallEndTime"
                      label={t('labels.endTime')}
                      required
                      isBlackLabel
                    >
                      <TimeSelectBox />
                    </BaseFormItem>
                  </BaseGrid>
                )}
              </BaseGrid>

              <BaseGrid columns={isCcmLimitedHours ? 2 : 1}>
                <BaseFormItem
                  name="ccmDoNotCallRestrictionType"
                  label={`CCM ${t('labels.doNotCallRestrictionType')}`}
                  isBlackLabel
                >
                  <DoNotCallRestrictionTypeSelectBox
                    type={dncRestrictionType}
                    withNull
                  />
                </BaseFormItem>
                {isCcmLimitedHours && (
                  <BaseGrid columns={2}>
                    <BaseFormItem
                      name="ccmDoNotCallStartTime"
                      label={t('labels.startTime')}
                      required
                      isBlackLabel
                    >
                      <TimeSelectBox />
                    </BaseFormItem>
                    <BaseFormItem
                      name="ccmDoNotCallEndTime"
                      label={t('labels.endTime')}
                      required
                      isBlackLabel
                    >
                      <TimeSelectBox />
                    </BaseFormItem>
                  </BaseGrid>
                )}
              </BaseGrid>

              <BaseFormItem
                name="notes"
                label={t('labels.note')}
                required
                isBlackLabel
              >
                <BaseRichTextEditor
                  value={values.notes ?? ''}
                  setValue={(value) => {
                    formik.setFieldValue('notes', value);
                  }}
                  disabled={false}
                />
              </BaseFormItem>
            </div>
          </BaseGrid>
        </BaseExpandAnimation>
      </BaseFormGroup>
    </BaseForm>
  );
});

ClinicInstructionsForm.displayName = FormDisplayName;

export default ClinicInstructionsForm;
